import React, { FunctionComponent, useContext } from 'react';
import { Box } from '@wix/design-system';
import { ArticleSortType, Category, Label } from '@wix/answers-api';
import { SubHeader } from '../SubHeader/SubHeader';
import { SEO } from '../SEO';
import { PAGES } from '../../constants';
import {
  PageType,
  PageSeo,
  RoadmapFilterType,
  RoadmapFeatureResolution,
  ArticleSearchResult,
} from '../../types';
import { UserEngagement } from '../UserEngagement';
import { AgentToolbar } from '../AgentToolbar';
import { Context } from '../../context';
import { isRoadmapArticle } from '../../utils/roadmap';
import { PageView } from '../PageView';
import css from './index.module.scss';
import type { Article as SearchProxyArticle } from '@wix/ambassador-answers-search-proxy/types';

export type PageDataArticle = SearchProxyArticle & {
  withRelatedArticles?: boolean;
};

export type PageData = {
  article?: PageDataArticle;
  category?: Category;
  roadmapData?: RoadmapPageData;
  searchResults?: ArticleSearchResult;
  term?: string;
};

export type RoadmapPageData = {
  sortType?: ArticleSortType;
  filterType?: RoadmapFilterType;
  resolution?: RoadmapFeatureResolution;
  roadmapLabels?: Label[];
  label?: { name: string; id: string };
};

export type PageTemplateData = {
  pageData?: PageData;
  itemId?: string;
  itemType?: string;
  hasError?: boolean;
};

type PageTemplateProps = {
  children: React.ReactNode;
  pageType: PageType;
  pageSeo: PageSeo;
};

export const PageTemplate: FunctionComponent<PageTemplateProps> = ({
  children,
  pageType,
  pageSeo,
}) => {
  const { pageTemplateData } = useContext(Context);

  const {
    pageData = {},
    itemId,
    itemType,
    hasError = false,
  } = pageTemplateData;

  const isRoadmapArticlePage =
    pageData.article &&
    pageData.article.resolution &&
    pageData.article.labels &&
    pageData.roadmapData?.roadmapLabels &&
    isRoadmapArticle(
      pageData.article.resolution,
      pageData.article.labels as Label[],
      pageData.roadmapData?.roadmapLabels
    );

  return (
    <Box direction="vertical" className={css.wrapper}>
      <SEO pageData={pageData} pageType={pageType} pageSeo={pageSeo} />
      <AgentToolbar pageData={pageData} pageType={pageType} />
      {pageType !== PAGES.HOMEPAGE && !hasError && (
        <SubHeader
          pageType={pageType}
          itemId={itemId}
          itemType={itemType}
          isRoadmapArticlePage={!!isRoadmapArticlePage}
          roadmapLabels={
            pageData.roadmapData
              ? pageData.roadmapData.roadmapLabels
              : undefined
          }
        />
      )}
      <UserEngagement
        pageData={pageData}
        pageType={pageType}
        itemType={itemType as string}
        itemId={itemId}
      />
      <PageView pageData={pageData} pageType={pageType} itemId={itemId} />
      {children}
    </Box>
  );
};
